import { StoreRule } from '@osos/shared/api';
import {
  ApplicationSharingPresenter,
  DataEntityTemplatePresenterModel,
  DataEntityWorkflowPresenterModel,
  InternalEntityPresenterModel,
  LiteDataModulePresenterModel,
  LocalizedValueModel,
  PermissionModel,
} from '..';
import { DataEntityStateEnum } from '../enums/data-entity-state.enum';
import { DataEntityTypeEnum } from '../enums/data-entity-type.enum';
import { DataTypeEnum } from '../enums/data-type.enum';

import { DataEntityExtensionPresenterModel } from './data-entity-extension-presenter.model';
import { DataEntityIndexPresenterModel } from './data-entity-indexes-presenter.model';
import {
  FieldPresenterModel,
  LiteFieldPresenterModel,
} from './field-presenter.model';
import { InternalCollectionPresenterModel } from './internal-collection-presenter.model';
import { SingularPluralModel } from './singular-plural.model';

export class DataEntityPresenterModel {
  specificationVersion?: number;

  id?: string;
  name: LocalizedValueModel;
  description?: LocalizedValueModel;
  normalizedName?: string;
  icon: {
    name: string;
  };
  fields: FieldPresenterModel[];
  state?: DataEntityStateEnum;
  // @Amer --RR not exists in backend
  status?: string;

  validations?: {}; // Jsonlogic according to specification
  isImplicit: boolean;
  type: DataEntityTypeEnum;
  typeDetails?: DataEntityProxyDetailsDto | DataEntityProxyTypeDetailsDto;
  typeOptions?: ADTOptions | VirtualProxyEntityOptions;
  internalEntities?: InternalEntityPresenterModel[];
  internalCollections?: InternalCollectionPresenterModel[];
  workflows: DataEntityWorkflowPresenterModel[];
  meta?: any;
  module?: string;
  moduleDetails?: LiteDataModulePresenterModel;
  entriesCount?: number;
  displayOption?: number;
  permissions?: Array<PermissionModel>;
  version: number;
  indexes?: Array<DataEntityIndexPresenterModel>;
  extentions?: DataEntityExtensionPresenterModel;
  entryNames?: SingularPluralModel;
  formSettings?: DataEntityFromSettingsModel;
  implicitOptions?: DataEntityIsImplicitOptionsModel;
  virtual?: { enabled: boolean; source: string };
  templates?: DataEntityTemplatePresenterModel[];
  treeStructure?: EntityTreeStructurePresenterModel;
  dataSources?: Array<DataEntityFieldSourcesPresenterModel>;
  storeRules?: StoreRule[];
  sharing?: ApplicationSharingPresenter;
  updateIdentifier?: string;
  metaFields?: FieldPresenterModel[];
  displayTemplate?: EntityDisplayTemplateModel;
}
export class EntityDisplayTemplateModel {
  parameters?: null | TemplateParameterTypesModel;
  templateId: string;
}
export type TemplateParameterTypesModel = {
  listEntry?: TemplateParameterModel[];

  viewEntry?: TemplateParameterModel[];
  editEntry?: TemplateParameterModel[];
};
export class TemplateParameterModel {
  /**
   * The name of the parameter matching the parameter name in template
   */
  name: string;

  /**
   * The value for the token. This is different based on the type property
   *     If type is Fixed, then the value is the fixed value
   *     If type is Expression, then the value is the expression. This is entry expression such as value.my_block[scope.index].my_field
   */
  value?: any;

  /**
   * The value type of the parameter. This can be one of the following:
   *     Fixed: The value is fixed and will not change
   *     Expression: The value is an expression that will be evaluated to get the value
   */
  valueType: TemplateParameterValueType;

  component?: string;

  source?: string;
}
export enum TemplateParameterValueType {
  Fixed = 0,
  Expression = 1,
  Field = 2,
}
export class DataEntityFromSettingsModel {
  hideFieldsIcon?: boolean;
}

export class DataEntityIsImplicitOptionsModel {
  allowEdit?: boolean;
  isReadOnly?: boolean;
}

export type LiteDataEntityPresenterModel = Pick<
  DataEntityPresenterModel,
  'id' | 'name' | 'normalizedName' | 'specificationVersion'
>;

export interface ADTOptions {
  adtType?: DataTypeEnum;
}
export class DataEntityProxyDetailsDto {
  sourceEntity?: LiteDataEntityPresenterModel;
  sourceModule?: LiteDataModulePresenterModel;
}
export class DataEntityProxyTypeDetailsDto {
  sources: DataEntityAdditionalSourceDetails[];
  primarySource: LiteDataEntityPresenterModel;
}
export class DataEntityAdditionalSourceDetails {
  //The alias for the relation
  alias: string;
  //The relation details with the data entity
  relation: RelationRelatedData;
}
export interface RelationRelatedData {
  relatedData: DataRelationRelatedData;
  name: LocalizedValueModel;
}
export interface DataRelationRelatedData {
  targetEntity: LiteDataEntityPresenterModel;
  targetField: LiteFieldPresenterModel;
  dataEntity: LiteDataEntityPresenterModel;
}

/**
 * in entity.type we have two types related indicate that this entity is build upon other entities
 * 1- Proxy 2- joiner
 * Proxy: is the simplest case where it's an entity built on top of entity outside the module
 * Joiner: in an entity built on top of entity and can join more entities through its relations
 * Since proxy is the more general case, we will name the models related to this topic with proxy
 */
export interface VirtualProxyEntityOptions {
  /**
   * The list of sources to be joined. The first item in the array is the primary entity.
   * This is a flat array of sources. The parent of each source is based on the scope[0]
   */
  sources: VirtualProxyEntityOptionsSource[];
}

export interface VirtualProxyEntityOptionsSource {
  /**
   * The alias of the source in the joined entity. This will be the relation normalized name followed by a number to avoid conflicts in case the type is Additional.
   * In case it is primary, the alias will be null
   * This is used as a perfix for the fields normalized name
   * The field normalized names will be in the following format:
   * alias + "$" + fieldNormalizedName
   */
  alias: string; // unique
  /**
   * The type of the source Primary or Additional
   */
  type: ProxyVirtualEntitySourceTypeEnum;
  /**
   * The entity normalized name
   */
  sourceEntityNormalizedName: string;
  /**
   * The query source Id to be used as a source for the entity. If not set, the source will be all the entires in sourceEntity
   */
  sourceQuery?: string | null; // or query name
  /**
   * The scope of the source entity to be joined.
   * The scope is an array of {alias - relation_normalized_name} to be used in the join query
   */
  scope?: {
    /**
     * The alias of the parent source.
     */
    alias: string;
    /**
     * The relation normalized name.
     */
    relation: string;
  } | null;
  /**
   * The level of the source. This will be parent level + 1.
   * The Primary source level will be 1.
   */
  level?: number;
  isMandatory?: boolean;
}

export enum ProxyVirtualEntitySourceTypeEnum {
  Primary = 0,
  Additional = 1,
}

export interface EntityTreeStructurePresenterModel {
  /**
   * Whether the tree is enabled or not
   */
  supported: boolean;
  /**
   * The field normalized name for the parent field
   */
  fields?: Array<{
    [key: string]: string;
  }>;
}

export class DataEntityFieldSourcesPresenterModel {
  mappedFields?: Array<MappingFields>;
  referenceEntity: string;
  referenceField: string;
}

export class MappingFields {
  fromFieldId: string;
  toFieldId: string;
}
